/**
 * Client for the Pages-Functions artist-selection API.
 *
 * Public read endpoints (GET /api/artist-pois, /api/artist-selection) are
 * open. Write endpoints live under /api/admin/* and are gated by Cloudflare
 * Access — the browser carries the Access session cookie automatically once
 * the user signs in via the email + one-time-PIN flow.
 */
import type { CustomPOI } from '../types/custom-poi';
import type { AntwerpLandmarkCategory } from '../types/antwerp-landmark';

/** Server's JSON shape for a POI. */
interface APIPoi {
  id: string;
  name: string;
  category: string;
  period: string;
  coordinates: [number, number];
  oneLiner: string;
  description: string;
  artistBrief: string;
  stillStands: boolean;
  heroImage: string | null;
  createdAt: number;
  updatedAt: number;
}

function apiToCustomPOI(p: APIPoi): { poi: CustomPOI; heroImage: string | null } {
  const poi: CustomPOI = {
    id: p.id,
    name: p.name,
    category: p.category as AntwerpLandmarkCategory,
    period: p.period,
    coordinates: p.coordinates,
    oneLiner: p.oneLiner,
    description: p.description,
    stillStands: p.stillStands,
    isCustom: true,
    createdAt: p.createdAt,
    ...(p.artistBrief.length > 0 ? { artistDirection: p.artistBrief } : {}),
  };
  return { poi, heroImage: p.heroImage };
}

export async function fetchAllPOIs(): Promise<
  { poi: CustomPOI; heroImage: string | null }[]
> {
  const res = await fetch('/api/artist-pois');
  if (!res.ok) throw new Error(`fetchAllPOIs: ${res.status}`);
  const data = (await res.json()) as { pois: APIPoi[] };
  return data.pois.map(apiToCustomPOI);
}

export async function fetchSelectionIds(): Promise<string[]> {
  const res = await fetch('/api/artist-selection');
  if (!res.ok) throw new Error(`fetchSelectionIds: ${res.status}`);
  const data = (await res.json()) as { ids: string[] };
  return data.ids;
}

/**
 * Return the signed-in user's email, or null if they aren't signed in.
 *
 * The "redirect: 'manual'" treatment is important: when Cloudflare Access
 * doesn't recognise the request, it responds with a 302 to its sign-in page.
 * fetch() would normally follow that and we'd receive the HTML form. Telling
 * fetch to return the redirect as opaque lets us detect the unauth case
 * without parsing HTML.
 */
export async function whoami(): Promise<string | null> {
  try {
    const res = await fetch('/api/admin/whoami', {
      credentials: 'include',
      redirect: 'manual',
    });
    if (res.type === 'opaqueredirect' || res.status === 0 || res.status === 401) return null;
    if (!res.ok) return null;
    const data = (await res.json()) as { email?: string };
    return data.email ?? null;
  } catch {
    return null;
  }
}

export async function savePOI(poi: CustomPOI, heroImage: string | null): Promise<void> {
  const body = {
    id: poi.id,
    name: poi.name,
    category: poi.category,
    period: poi.period,
    lng: poi.coordinates[0],
    lat: poi.coordinates[1],
    oneLiner: poi.oneLiner,
    description: poi.description,
    artistBrief: poi.artistDirection ?? '',
    stillStands: poi.stillStands,
    heroImage,
  };
  const res = await fetch('/api/admin/artist-pois', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 401) {
    throw new Error('Your sign-in expired. Click Sign in again.');
  }
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(`Save failed (${res.status}): ${msg}`);
  }
}

export async function deletePOI(id: string): Promise<void> {
  const res = await fetch(`/api/admin/artist-pois/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (res.status === 401) {
    throw new Error('Your sign-in expired. Click Sign in again.');
  }
  if (!res.ok && res.status !== 204) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(`Delete failed (${res.status}): ${msg}`);
  }
}

export async function replaceSelection(ids: string[]): Promise<void> {
  const res = await fetch('/api/admin/artist-selection', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (res.status === 401) {
    throw new Error('Your sign-in expired. Click Sign in again.');
  }
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(`Selection update failed (${res.status}): ${msg}`);
  }
}

// ─── Landmark overrides ──────────────────────────────────────────────────

export type LandmarkOverrideFields = Partial<{
  name: string;
  oneLiner: string;
  description: string;
  artistDirection: string;
  folklore: string;
  period: string;
  category: string;
}>;

export interface LandmarkOverride {
  fields: LandmarkOverrideFields;
  updatedAt: number;
  updatedBy: string;
}

export async function fetchLandmarkOverrides(): Promise<Record<string, LandmarkOverride>> {
  const res = await fetch('/api/landmark-overrides');
  if (!res.ok) throw new Error(`fetchLandmarkOverrides: ${res.status}`);
  const data = (await res.json()) as { overrides: Record<string, LandmarkOverride> };
  return data.overrides;
}

/**
 * Save a partial override for a landmark. To revert a single field to its
 * baseline (i.e. remove that key from the override), pass `null` as its value.
 */
export async function patchLandmarkOverride(
  id: string,
  fields: Record<string, string | null>,
): Promise<LandmarkOverride> {
  const res = await fetch(`/api/admin/landmark-overrides/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fields }),
  });
  if (res.status === 401) throw new Error('Your sign-in expired. Click Sign in again.');
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(`Save failed (${res.status}): ${msg}`);
  }
  return (await res.json()) as LandmarkOverride;
}

export async function deleteLandmarkOverride(id: string): Promise<void> {
  const res = await fetch(`/api/admin/landmark-overrides/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (res.status === 401) throw new Error('Your sign-in expired. Click Sign in again.');
  if (!res.ok && res.status !== 204) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(`Delete failed (${res.status}): ${msg}`);
  }
}

// ─── Paper frame (artist briefing rectangle) ─────────────────────────────

export interface ServerPaperFrame {
  centerLng: number;
  centerLat: number;
  shortSideMeters: number;
  rotationDeg: number;
  locked: boolean;
  updatedAt: number;
}

/**
 * Public read — returns the locked, published frame visible to everyone.
 * Returns null when no frame has been locked yet (artist sees nothing).
 */
export async function fetchPublicPaperFrame(): Promise<ServerPaperFrame | null> {
  const res = await fetch('/api/paper-frame');
  if (!res.ok) throw new Error(`fetchPublicPaperFrame: ${res.status}`);
  const data = (await res.json()) as { frame: ServerPaperFrame | null };
  return data.frame;
}

/** Admin read — returns the full record including draft (unlocked) state. */
export async function fetchAdminPaperFrame(): Promise<ServerPaperFrame | null> {
  const res = await fetch('/api/admin/paper-frame', { credentials: 'include' });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`fetchAdminPaperFrame: ${res.status}`);
  const data = (await res.json()) as { frame: ServerPaperFrame | null };
  return data.frame;
}

export async function saveAdminPaperFrame(frame: {
  centerLng: number;
  centerLat: number;
  shortSideMeters: number;
  rotationDeg: number;
  locked: boolean;
}): Promise<ServerPaperFrame> {
  const res = await fetch('/api/admin/paper-frame', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(frame),
  });
  if (res.status === 401) {
    throw new Error('Your sign-in expired. Click Sign in again.');
  }
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(`Save failed (${res.status}): ${msg}`);
  }
  const data = (await res.json()) as { frame: ServerPaperFrame };
  return data.frame;
}

/** Navigate to the Cloudflare Access sign-in, returning to the SPA after auth. */
export function signIn(): void {
  if (typeof window === 'undefined') return;
  // Access redirects the browser through its email + PIN flow, then back to
  // this URL. /api/admin/whoami then returns an HTML bounce to "/" so the
  // user lands back in the SPA.
  window.location.href = '/api/admin/whoami?redirect=/';
}

/** Clear the Cloudflare Access session and reload the SPA. */
export function signOut(): void {
  if (typeof window === 'undefined') return;
  // The /cdn-cgi/access/logout endpoint is Cloudflare-hosted on the same
  // domain; it clears the CF_Authorization cookie, then redirects.
  window.location.href = '/cdn-cgi/access/logout?returnTo=/';
}
